const OAUTH_AVATAR_FIELDS = ["avatar_url", "picture", "photo_url"] as const;

export interface OAuthProfileBasics {
  firstName: string | null;
  lastName: string | null;
  avatarUrl: string | null;
}

export interface OAuthIdentityLike {
  provider?: string | null;
  identity_data?: unknown;
}

export function getOAuthProfileBasics(
  metadata: Record<string, unknown> | null | undefined,
): OAuthProfileBasics {
  const givenName = readString(metadata, "given_name", "first_name");
  const familyName = readString(metadata, "family_name", "last_name");
  const fullName = readString(metadata, "full_name", "name");
  const parsed = splitName(fullName);

  return {
    firstName: givenName ?? parsed.firstName,
    lastName: familyName ?? parsed.lastName,
    avatarUrl: getOAuthAvatarUrl(metadata),
  };
}

export function getGoogleIdentityProfileBasics(
  identities: readonly OAuthIdentityLike[] | null | undefined,
): OAuthProfileBasics | null {
  const googleIdentity = identities?.find((identity) => identity.provider === "google");
  if (!googleIdentity) return null;

  const metadata = googleIdentity.identity_data && typeof googleIdentity.identity_data === "object"
    ? googleIdentity.identity_data as Record<string, unknown>
    : null;

  return getOAuthProfileBasics(metadata);
}

export function hasGoogleProfileData(profile: OAuthProfileBasics | null | undefined): boolean {
  return !!profile && !!(profile.firstName || profile.lastName || profile.avatarUrl);
}

export interface OAuthAuthUserLike {
  identities?: readonly OAuthIdentityLike[] | null;
  user_metadata?: unknown;
}

/** Prefer Google identity_data, then fall back to auth user_metadata. */
export function resolveOAuthProfileBasicsFromUser(
  user: OAuthAuthUserLike | null | undefined,
): OAuthProfileBasics | null {
  if (!user) return null;

  const fromIdentity = getGoogleIdentityProfileBasics(user.identities);
  const fromMetadata = getOAuthProfileBasics(
    user.user_metadata && typeof user.user_metadata === "object"
      ? user.user_metadata as Record<string, unknown>
      : null,
  );

  if (!fromIdentity && !fromMetadata) return null;

  return {
    firstName: fromIdentity?.firstName || fromMetadata.firstName || null,
    lastName: fromIdentity?.lastName || fromMetadata.lastName || null,
    avatarUrl: fromIdentity?.avatarUrl || fromMetadata.avatarUrl || null,
  };
}

export interface OAuthProfileFieldSnapshot {
  firstName?: string | null;
  lastName?: string | null;
  avatarUrl?: string | null;
}

export function buildOAuthProfilePatch(
  current: OAuthProfileFieldSnapshot,
  oauth: OAuthProfileBasics,
): Record<string, string> {
  const patch: Record<string, string> = {};

  if (!current.firstName?.trim() && oauth.firstName) {
    patch.first_name = oauth.firstName;
  }
  if (!current.lastName?.trim() && oauth.lastName) {
    patch.last_name = oauth.lastName;
  }
  if (!current.avatarUrl?.trim() && oauth.avatarUrl) {
    patch.avatar_url = oauth.avatarUrl;
  }

  return patch;
}

export function mergeOAuthProfileFields(
  current: OAuthProfileFieldSnapshot,
  oauth: OAuthProfileBasics | null,
): Required<OAuthProfileFieldSnapshot> {
  return {
    firstName: current.firstName?.trim() || oauth?.firstName || "",
    lastName: current.lastName?.trim() || oauth?.lastName || "",
    avatarUrl: current.avatarUrl?.trim() || oauth?.avatarUrl || "",
  };
}

export function getOAuthAvatarUrl(metadata: Record<string, unknown> | null | undefined): string | null {
  if (!metadata) return null;

  for (const field of OAUTH_AVATAR_FIELDS) {
    const value = metadata[field];
    if (typeof value !== "string") continue;
    const trimmed = value.trim();
    if (isSafeRemoteImageUrl(trimmed)) return trimmed;
  }

  return null;
}

function isSafeRemoteImageUrl(value: string): boolean {
  try {
    const url = new URL(value);
    return url.protocol === "https:" || url.protocol === "http:";
  } catch {
    return false;
  }
}

function readString(
  metadata: Record<string, unknown> | null | undefined,
  ...keys: string[]
): string | null {
  if (!metadata) return null;
  for (const key of keys) {
    const value = metadata[key];
    if (typeof value !== "string") continue;
    const trimmed = value.trim();
    if (trimmed) return trimmed;
  }
  return null;
}

function splitName(fullName: string | null): { firstName: string | null; lastName: string | null } {
  if (!fullName) return { firstName: null, lastName: null };
  const parts = fullName.trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return { firstName: null, lastName: null };
  if (parts.length === 1) return { firstName: parts[0], lastName: null };
  return {
    firstName: parts[0],
    lastName: parts.slice(1).join(" "),
  };
}
